// Lens Module
import Vue from 'vue'
import LensAPI from '@/api/lens'
import _ from 'lodash'
import lineColors from '../../utils/lens/selectedMetricColors'
import store from '@/store'
import FirebaseAPI from '@/api/firebase'
import { isWithinDaysAgo } from '../../utils/dateUtils'
import { getDemoSidebarLens, getDemoProviders, getDemoLens, getDemoLensStatus } from '@/api/lens/demo/LensDemoAPI'
import { DEMO_LENS_ID, DEMO_BUSINESS_IDS } from '@/utils/lens/demo/lensDemoConstants'

function isDemoActive (state) {
  return !!(state && state.isDemoLensActive)
}

function mergeDemoIntoLenses (state, lenses = []) {
  const withoutDemo = (lenses || []).filter((lens) => lens.id !== DEMO_LENS_ID)
  if (!isDemoActive(state)) return withoutDemo
  return [getDemoLens(), ...withoutDemo]
}

function mergeDemoIntoSidebar (state, lenses = []) {
  const withoutDemo = (lenses || []).filter((lens) => lens.id !== DEMO_LENS_ID)
  if (!isDemoActive(state)) return withoutDemo
  return [getDemoSidebarLens(), ...withoutDemo]
}

function mergeDemoIntoFolders (state, folders = {}) {
  const next = { ...folders }
  if (!isDemoActive(state)) {
    delete next[DEMO_LENS_ID]
    return next
  }
  next[DEMO_LENS_ID] = getDemoSidebarLens().folders
  return next
}

function mergeDemoIntoProviders (state, providers = []) {
  const withoutDemo = (providers || []).filter((provider) => (
    provider.business_id !== DEMO_BUSINESS_IDS.META && provider.business_id !== DEMO_BUSINESS_IDS.TIKTOK
  ))
  if (!isDemoActive(state)) return withoutDemo
  return [...getDemoProviders(), ...withoutDemo]
}

function mergeDemoIntoStatus (state, statuses = {}) {
  const next = { ...statuses }
  if (!isDemoActive(state)) {
    delete next[DEMO_LENS_ID]
    return next
  }
  const status = getDemoLensStatus()
  next[DEMO_LENS_ID] = {
    isConnected: true,
    isAdAccountsConnected: true,
    isProvidersConnected: true,
    isBackfill: false,
    accessType: status.access_type
  }
  return next
}

export default {
  namespaced: true,
  state: {
    activeLensId: null,

    columnDrawerView: false,
    lenses: [],
    sidebarLenses: [],
    providers: [],
    lensFolders: {}, // key: lensId, value: [folders]
    colorFormat: '',
    isLoadingSidebarLenses: false,

    mappedRowColors: {
      bar: {},
      line: {}
    },

    // Pagination
    pagination: {
      currentPage: 1,
      limit: 10,
      totalItems: 0,
      totalPages: 0
    },
    showLensCreationModal: false,
    showReportCreationModal: null, // Will be set to init folder id
    showReportShareModal: false,

    // If a report is updated/deleted in sidebar, we need to update the active page (if applicable)
    pendingLocalReportUpdate: null,

    providersConnectionStatus: {
      allConnected: true,
      disconnected: []
    },
    lensConnectionsStatus: {},

    showEditTagModal: false,
    tags: [],
    isDemoLensActive: false
  },
  getters: {
    getActiveLensId: state => state.activeLensId,
    getIsDemoLensActive: state => state.isDemoLensActive,
    getLenses: state => state.lenses,
    getSidebarLenses: state => state.sidebarLenses,
    getIsLoadingSidebarLenses: state => state.isLoadingSidebarLenses,
    getProviders: state => state.providers,
    getProvidersConnectionStatus: state => state.providersConnectionStatus,
    getLensFolders: state => state.lensFolders,
    getColumnDrawerView: state => state.columnDrawerView,
    getColorFormat: state => state.colorFormat,
    getPagination: state => state.pagination,
    getShowLensCreationModal: state => state.showLensCreationModal,
    getShowReportCreationModal: state => state.showReportCreationModal,
    getShowReportShareModal: state => state.showReportShareModal,
    getMappedRowColor: state => state.mappedRowColors,
    getPendingLocalReportUpdate: state => state.pendingLocalReportUpdate,
    getLensConnectionsStatus: state => state.lensConnectionsStatus,
    getLensTags: state => state.tags,
    getShowEditTagModal: state => state.showEditTagModal
  },
  mutations: {
    SET_ACTIVE_LENS_ID (state, lensId) {
      state.activeLensId = lensId
    },
    SET_LENSES (state, lenses) {
      state.lenses = lenses
    },
    SET_SIDEBAR_LENSES (state, lenses) {
      state.sidebarLenses = lenses
    },
    SET_IS_LOADING_SIDEBAR_LENSES (state, isLoading) {
      state.isLoadingSidebarLenses = isLoading
    },
    SET_PROVIDERS (state, providers) {
      state.providers = providers
    },
    SET_PROVIDERS_CONNECTION_STATUS (state, connectionStatus) {
      state.providersConnectionStatus = connectionStatus
    },
    SET_LENS_FOLDERS (state, lensFolders) {
      state.lensFolders = { ...lensFolders }
    },
    SET_COLUMN_DRAWER_VIEW (state, canShow) {
      state.columnDrawerView = canShow
    },
    SET_COLOR_FORMAT (state, colorFormat) {
      state.colorFormat = colorFormat
    },
    SET_PAGINATION (state, pagination) {
      state.pagination = { ...pagination }
    },
    SET_SHOW_LENS_CREATION_MODAL (state, show) {
      state.showLensCreationModal = show
    },
    SET_SHOW_REPORT_CREATION_MODAL (state, folderId) {
      state.showReportCreationModal = folderId
    },
    SET_SHOW_REPORT_SHARE_MODAL (state, show) {
      state.showReportShareModal = show
    },
    SET_MAPPED_ROW_COLOR (state, data) {
      const { newMappedColors, graph } = data
      if (graph) {
        state.mappedRowColors[graph] = newMappedColors
      } else {
        state.mappedRowColors = newMappedColors
      }
    },
    SET_PENDING_LOCAL_REPORT_UPDATE (state, update) {
      state.pendingLocalReportUpdate = update
    },
    SET_LENS_CONNECTIONS_STATUS (state, connectionStatuses) {
      state.lensConnectionsStatus = connectionStatuses
    },
    SET_TAGS (state, tags) {
      state.tags = tags
    },
    SET_SHOW_EDIT_TAG_MODAL (state, show) {
      state.showEditTagModal = show
    },
    SET_DEMO_LENS_ACTIVE (state, isActive) {
      state.isDemoLensActive = isActive
    }
  },
  actions: {
    updateColorFormat ({ commit }, key) {
      commit('SET_COLOR_FORMAT', key)
    },
    updatePagination ({ commit }, pagination) {
      commit('SET_PAGINATION', pagination)
    },
    async fetchLenses ({ state, commit }) {
      let lensData = []
      // improve later
      try {
        const { data } = await LensAPI.Lens.getAll()
        lensData = data
        commit('SET_LENSES', mergeDemoIntoLenses(state, lensData))
      } catch (e) {
        console.error('Error fetching lenses:', e)
        commit('SET_LENSES', mergeDemoIntoLenses(state, []))
      }
    },
    async fetchSidebarLenses ({ state, commit }) {
      commit('SET_IS_LOADING_SIDEBAR_LENSES', true)
      try {
        const [
          { data: sidebarData = [] },
          { data: statusData = [] },
          { data: providerData = [] }
        ] = await Promise.all([
          LensAPI.Lens.getAllSidebar(),
          LensAPI.Lens.getAllLensStatusV2(),
          LensAPI.Lens.getPlatformAccounts()
        ])

        const lensFolders = {}
        const lensConnectionsStatus = {}
        sidebarData.forEach(lens => {
          const folders = [...lens.folders].sort((a, _) => a.name === 'Default' ? -1 : 0)
          lensFolders[lens.id] = folders
        })
        // Do a status sweep to determine how the lenses are doing
        statusData.forEach(status => {
          let allAdAccountsConnected = true
          let allProvidersConnected = true
          let totalPercentDiff = 0
          status.ad_accounts.forEach(account => {
            const adAccountProvider = providerData.find(provider => provider.business_id === account.business_id)
            if (!account.is_valid) allAdAccountsConnected = false
            if (!adAccountProvider?.is_valid) allProvidersConnected = false
            totalPercentDiff += account.data_accuracy.percent_diff
          })
          const avgPercentDiff = totalPercentDiff / status.ad_accounts.length
          lensConnectionsStatus[status.lens_id] = {
            isConnected: status.ad_accounts.length && allAdAccountsConnected && allProvidersConnected,
            isAdAccountsConnected: allAdAccountsConnected,
            isProvidersConnected: allProvidersConnected,
            isBackfill: avgPercentDiff > 10,
            accessType: status.access_type
          }
        })

        // Do a check for disconnected provider accounts
        let disconnectedProviders = providerData?.filter(provider => !provider.is_valid) ?? []
        const connectionOwners = await Promise.all(disconnectedProviders.map(async (provider) => {
          const user = await FirebaseAPI.Users.getPublicById(provider.user_id)
          return {
            name: user?.name || (user?.first_name ? `${user.first_name} ${user.last_name}` : 'Unknown User'),
            email: user.email,
            avatar: user?.avatar,
            user_id: provider.user_id
          }
        }))
        disconnectedProviders = disconnectedProviders.map(provider => ({
          ...provider,
          connectionOwner: connectionOwners.find(owner => owner.user_id === provider.user_id)
        }))

        commit('SET_PROVIDERS_CONNECTION_STATUS', {
          allConnected: disconnectedProviders.length === 0,
          disconnected: disconnectedProviders
        })
        commit('SET_SIDEBAR_LENSES', mergeDemoIntoSidebar(state, sidebarData.map(lens => {
          const status = lensConnectionsStatus[lens.id]
          return {
            ...lens,
            accessType: status?.accessType,
            isDataReady: !status?.isBackfill,
            isDisconnected: !status?.isConnected
          }
        })))
        commit('SET_LENS_FOLDERS', mergeDemoIntoFolders(state, lensFolders))
        commit('SET_LENS_CONNECTIONS_STATUS', mergeDemoIntoStatus(state, lensConnectionsStatus))
        commit('SET_PROVIDERS', mergeDemoIntoProviders(state, providerData))
      } catch (e) {
        console.error('Error fetching sidebar lenses:', e)
        commit('SET_SIDEBAR_LENSES', mergeDemoIntoSidebar(state, []))
        commit('SET_LENS_FOLDERS', mergeDemoIntoFolders(state, {}))
        commit('SET_PROVIDERS', mergeDemoIntoProviders(state, state.providers))
        commit('SET_LENS_CONNECTIONS_STATUS', mergeDemoIntoStatus(state, state.lensConnectionsStatus))
      } finally {
        commit('SET_IS_LOADING_SIDEBAR_LENSES', false)
      }
    },
    // TODO @Sam: clean up
    updateRowColor ({ state, commit }, { keys, isAdding, graph, ignorePrevColors }) {
      if (graph === 'grid') return
      // Callers pass either row objects or raw row keys, so normalize before mapping colors
      const rowKeys = (keys ?? []).map((key) => typeof key === 'string' ? key : key?.group_by)
      const mapCopy = { ...state.mappedRowColors }

      const usedColors = Object.values(mapCopy[graph])
      const lineColorCopy = [...lineColors]
      const unusedColors = ignorePrevColors ? lineColorCopy : lineColorCopy.filter((col) => !usedColors.includes(col))
      // If applying to multiple (0 or > 1)
      if (rowKeys.length !== 1) {
        const newMappedColors = !ignorePrevColors && rowKeys.length > 0 ? { ...state.mappedRowColors[graph] } : {}
        if (rowKeys.length > 0) {
          if (isAdding) {
            rowKeys.forEach((rowKey) => {
              if (!newMappedColors[rowKey]) {
                newMappedColors[rowKey] = unusedColors.shift()
              }
            })
          } else {
            Object.keys(newMappedColors).forEach((key) => {
              if (!rowKeys.includes(key)) {
                delete newMappedColors[key]
              }
            })
          }
        }
        commit('SET_MAPPED_ROW_COLOR', { newMappedColors, graph })
      } else {
        // Applying to single checkbox
        const key = rowKeys[0]
        const newMappedColors = ignorePrevColors ? {} : { ...mapCopy[graph] }
        if (isAdding) {
          if (!newMappedColors[key]) newMappedColors[key] = unusedColors[0]
        } else {
          delete newMappedColors[key]
        }
        mapCopy[graph] = newMappedColors
        commit('SET_MAPPED_ROW_COLOR', { newMappedColors: mapCopy })
      }
    },
    // The demo Lens joins the sidebar/switcher lists while active; fetches
    // above keep merging it in until exitDemoLens
    enterDemoLens ({ state, commit }) {
      commit('SET_DEMO_LENS_ACTIVE', true)
      const demoLens = getDemoLens()
      const demoSidebar = getDemoSidebarLens()
      commit('SET_LENSES', mergeDemoIntoLenses({ isDemoLensActive: true }, state.lenses))
      commit('SET_SIDEBAR_LENSES', mergeDemoIntoSidebar({ isDemoLensActive: true }, state.sidebarLenses))
      commit('SET_LENS_FOLDERS', mergeDemoIntoFolders({ isDemoLensActive: true }, state.lensFolders))
      commit('SET_PROVIDERS', mergeDemoIntoProviders({ isDemoLensActive: true }, state.providers))
      commit('SET_LENS_CONNECTIONS_STATUS', mergeDemoIntoStatus({ isDemoLensActive: true }, state.lensConnectionsStatus))
      return { demoLens, demoSidebar }
    },
    exitDemoLens ({ state, commit }) {
      commit('SET_DEMO_LENS_ACTIVE', false)
      commit('SET_LENSES', (state.lenses || []).filter((lens) => lens.id !== DEMO_LENS_ID))
      commit('SET_SIDEBAR_LENSES', (state.sidebarLenses || []).filter((lens) => lens.id !== DEMO_LENS_ID))
      const folders = { ...state.lensFolders }
      delete folders[DEMO_LENS_ID]
      commit('SET_LENS_FOLDERS', folders)
      commit('SET_PROVIDERS', (state.providers || []).filter((provider) => (
        provider.business_id !== DEMO_BUSINESS_IDS.META && provider.business_id !== DEMO_BUSINESS_IDS.TIKTOK
      )))
      const statuses = { ...state.lensConnectionsStatus }
      delete statuses[DEMO_LENS_ID]
      commit('SET_LENS_CONNECTIONS_STATUS', statuses)
    },
    async fetchTags ({ state, commit }, lensId = null) {
      try {
        console.log('%c Fetching Tags', 'font-size: 14px; color: pink')
        const reqLensId = lensId ?? (state.activeLensId ?? null)
        const { data } = await LensAPI.Tags.getAll(reqLensId)
        const lensTags = data.map(tag => ({
          ...tag,
          color: tag.color || 'gray'
        }))
        commit('SET_TAGS', lensTags)
      } catch (error) {
        console.error('Error fetching tags', error)
      }
    }
  }
}
